import type { trackEvent } from '@svs/nova-core/nextjs/analytics';
import { getAccountBalance } from '@svs/nova-core/nextjs/auth';

import { MiddlewarePaymentError } from '../../error/MiddlewarePaymentError.js';
import type { InteractionPrePaymentMiddlewareFn } from '../../flow/types.js';
import { type OnInteraction, type Product } from '../../index.js';
import type { PreMiddlewareFn } from '../../payment/createPayment.js';
import { getReturnUrl } from '../../utils/getReturnUrl.js';
import { logger } from '../../utils/logger.js';
import type { Wager } from '../../wager/Wager.js';

const trackDepositAction = (product: Product | undefined, trackEventFunction?: typeof trackEvent): void => {
  if (!product) {
    logger.info('Product information is missing for tracking deposit action');
    return;
  }
  if (trackEventFunction) {
    trackEventFunction({
      category: 'overlays',
      action: 'deposit',
      optLabel: product,
    });
  } else {
    logger.info('Track event function is not available for tracking deposit action');
  }
};

type InteractionProps = {
  messages: string[];
  message?: string;
  title: string;
};

/**
 * Middleware to check the user's account balance before confirming a payment interaction.
 *
 * @param onInteraction The callback to trigger the interaction.
 * @param options Optional configuration options for the middleware.
 * @param options.paymentUrls Optional payment URLs for redirecting in payment middlewares.
 * @param options.currencyFormatter Optional function to format currency values for display in the interaction message.
 * @param options.trackEventFunction Optional function to track analytics events, used for tracking when users interact with the account balance dialog.
 * @returns The middleware function that checks the account balance.
 */
export function accountBalanceMw(
  onInteraction: OnInteraction<InteractionProps>,
  options?: {
    paymentUrls?: Record<string, string | undefined>;
    currencyFormatter?: (amount: number | string, options?: { shouldUseDecimals?: boolean; unit?: string }) => string;
    trackEventFunction?: typeof trackEvent;
  }
): PreMiddlewareFn<Wager> {
  const fn: ReturnType<InteractionPrePaymentMiddlewareFn<Wager>> =
    ({ payment }) =>
    async (next) => {
      try {
        const { paymentUrls, currencyFormatter, trackEventFunction } = options ?? {};
        const accountBalance = await getAccountBalance();
        const payableItems = payment.getPayableItems();
        const wagers = payableItems.filter((wager) => !wager.activatedDrawId);
        const totalPriceOfPlayerWagers = wagers.length > 0 ? wagers.reduce((sum, wager) => sum + wager.price, 0) : 0;
        logger.info(`Checking account balance`);
        if (accountBalance.accountBalance < totalPriceOfPlayerWagers) {
          logger.info(`Account balance too low: ${accountBalance.accountBalance}, price: ${totalPriceOfPlayerWagers}`);
          const wagerCount = payment.count() > 1 ? 'dina' : 'ditt';
          const title = `Du har inte tillräckligt med pengar på ditt spelkonto för att betala ${wagerCount} spel`;
          const messages = [
            `Saldo: ${currencyFormatter ? currencyFormatter(accountBalance.accountBalance, { shouldUseDecimals: true }) : accountBalance.accountBalance}`,
            `Pris: ${currencyFormatter ? currencyFormatter(totalPriceOfPlayerWagers) : totalPriceOfPlayerWagers}`,
          ];
          await onInteraction({ title, messages });
          const depositUrl = paymentUrls?.['transactionsHome'] ?? '/insattning';
          const returnUrl = getReturnUrl();
          const [firstWager] = wagers;
          if (firstWager) {
            trackDepositAction(firstWager.bets[0]?.product, trackEventFunction);
          }
          // Redirect to deposit page with return URL as query parameter, don't handle action=pay in this version of payment. Implemented in next version of payment.
          window.location.href = `${depositUrl}?returnUrl=${encodeURIComponent(returnUrl.toString())}`;
          next(
            new MiddlewarePaymentError({
              clientCode: 'NOT_ENOUGH_MONEY',
            })
          );
          return;
        }
      } catch (error) {
        if (error instanceof Error) {
          logger.debug('Payment confirmation aborted', { message: error.message, error });
        }
        const mwError = new MiddlewarePaymentError({
          clientCode: 'NOT_ENOUGH_MONEY',
        });
        next(mwError);
        return;
      }
      next();
    };

  return fn;
}
